import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el as elM, en as enM } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Risk {
  id: string;
  title: string;
  description: string;
  contract_reference?: string;
  risk_category?: string;
  risk_type?: 'threat' | 'opportunity';
  probability: number;
  impact: number;
  score: number;
  owner: string;
  mitigation: string;
  status: 'Open' | 'In Progress' | 'Closed';
  comments?: string;
  is_new?: boolean;
  added_by_document?: boolean;
  has_suggestion?: boolean;
  ai_suggestion?: {
    probability: number;
    impact: number;
    mitigation: string;
    reason: string;
  };
}

const CATEGORY_STYLES: Record<string, { backgroundColor: string; color: string }> = {
  'Χρονικό':           { backgroundColor: '#dbeafe', color: '#1e40af' },
  'Οικονομικό':        { backgroundColor: '#dcfce7', color: '#166534' },
  'Τεχνικό':           { backgroundColor: '#f3e8ff', color: '#6b21a8' },
  'Νομικό/Συμβατικό':  { backgroundColor: '#fee2e2', color: '#991b1b' },
  'Οργανωτικό':        { backgroundColor: '#fef9c3', color: '#854d0e' },
  'Εξωτερικό':         { backgroundColor: '#f1f5f9', color: '#475569' },
};
const getCategoryStyle = (cat: string) => CATEGORY_STYLES[cat] ?? { backgroundColor: '#f3f4f6', color: '#374151' };

const RiskManagement: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit, openCreate }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.risk;
  const C = M.common;
  const [risks, setRisks] = useState<Risk[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedSuggestion, setExpandedSuggestion] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Risk>>({
    title: '', description: '', probability: 0.5, impact: 5,
    owner: '', mitigation: '', status: 'Open', comments: '',
  });
  const [aiMitLoading, setAiMitLoading] = useState(false);
  const [stakeholderNames, setStakeholderNames] = useState<string[]>([]);
  const [hoveredRisk, setHoveredRisk] = useState<{ risk: Risk; x: number; y: number } | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | 'threat' | 'opportunity'>('all');

  useEffect(() => { fetchRisks(); fetchStakeholders(); }, []);
  useEffect(() => { if (openCreate && canEdit) setShowModal(true); }, [openCreate]);

  const fetchStakeholders = async () => {
    try {
      const res = await axios.get(`${API}/modules/${project.id}/Stakeholders`, { headers: { Authorization: `Bearer ${token}` } });
      const names: string[] = (res.data?.data?.stakeholders || []).map((s: any) => s.name).filter(Boolean);
      setStakeholderNames(names);
    } catch {}
  };

  const fetchRisks = async () => {
    try {
      const response = await axios.get(
        `${API}/modules/${project.id}/Risk%20Management`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.data?.risks) {
        const risksWithScore = response.data.data.risks.map((r: any, i: number) => ({
          ...r,
          id: r.id || `risk_${i}`,
          probability: parseFloat(r.probability) || 0.5,
          impact: parseFloat(r.impact) || 5,
          score: (parseFloat(r.probability) || 0.5) * (parseFloat(r.impact) || 5),
          owner: r.owner || '',
          status: r.status || 'Open',
        }));
        setRisks(risksWithScore.sort((a: Risk, b: Risk) => b.score - a.score));
      }
    } catch (err) {
      console.error('Error fetching risks:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveRisks = async (updatedRisks: Risk[]) => {
    await axios.put(
      `${API}/modules/${project.id}/Risk%20Management`,
      { data: { risks: updatedRisks }, userId: user.id },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const handleSubmit = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      const score = (form.probability || 0) * (form.impact || 0);
      const newRisk: Risk = {
        id: editingRisk?.id || `risk_${Date.now()}`,
        title: form.title || '',
        description: form.description || '',
        probability: form.probability || 0.5,
        impact: form.impact || 5,
        score,
        owner: form.owner || '',
        mitigation: form.mitigation || '',
        status: form.status || 'Open',
        comments: form.comments || '',
        risk_type: form.risk_type || 'threat',
      };
      let updatedRisks: Risk[];
      if (editingRisk) {
        updatedRisks = risks.map(r => r.id === editingRisk.id ? newRisk : r);
      } else {
        updatedRisks = [...risks, newRisk];
      }
      updatedRisks = updatedRisks.sort((a, b) => b.score - a.score);
      await saveRisks(updatedRisks);
      setRisks(updatedRisks);
      resetForm();
    } catch (err) {
      console.error('Error saving risk:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (riskId: string) => {
    const updatedRisks = risks.filter(r => r.id !== riskId);
    await saveRisks(updatedRisks);
    setRisks(updatedRisks);
  };

  const handleEdit = (risk: Risk) => {
    setEditingRisk(risk);
    setForm(risk);
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({ title: '', description: '', probability: 0.5, impact: 5, owner: '', mitigation: '', status: 'Open', comments: '', risk_type: 'threat' });
    setEditingRisk(null);
    setShowModal(false);
  };

  const acceptSuggestion = async (risk: Risk) => {
    if (!risk.ai_suggestion) return;
    const updated: Risk = {
      ...risk,
      probability: risk.ai_suggestion.probability,
      impact: risk.ai_suggestion.impact,
      score: risk.ai_suggestion.probability * risk.ai_suggestion.impact,
      mitigation: risk.ai_suggestion.mitigation,
      has_suggestion: false,
      ai_suggestion: undefined,
    };
    const updatedRisks = risks.map(r => r.id === risk.id ? updated : r).sort((a, b) => b.score - a.score);
    await saveRisks(updatedRisks);
    setRisks(updatedRisks);
    setExpandedSuggestion(null);
  };

  const rejectSuggestion = async (risk: Risk) => {
    const updated: Risk = { ...risk, has_suggestion: false, ai_suggestion: undefined };
    const updatedRisks = risks.map(r => r.id === risk.id ? updated : r);
    await saveRisks(updatedRisks);
    setRisks(updatedRisks);
    setExpandedSuggestion(null);
  };

  const confirmNewRisk = async (riskId: string) => {
    const updatedRisks = risks.map(r => r.id === riskId ? { ...r, is_new: false, added_by_document: false } : r);
    await saveRisks(updatedRisks);
    setRisks(updatedRisks);
  };

  const getScoreColor = (score: number) => {
    if (score >= 7) return { bg: '#fee2e2', text: '#991b1b', label: L.scoreHigh };
    if (score >= 4) return { bg: '#fef9c3', text: '#854d0e', label: L.scoreMedium };
    return { bg: '#dcfce7', text: '#166534', label: L.scoreLow };
  };

  const exportToExcel = () => {
    const [h0, h1, h2, h3, h4, h5, h6, h7, h8] = L.excelHeaders;
    const data = risks.map((r, i) => ({
      '#': i + 1, [h0]: r.title, [h1]: r.description,
      [h2]: r.probability, [h3]: r.impact,
      [h4]: r.score.toFixed(2), [h5]: getScoreColor(r.score).label,
      [h6]: r.owner, [h7]: r.mitigation, [h8]: r.status,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    ws['!cols'] = [{ wch: 4 }, { wch: 25 }, { wch: 40 }, { wch: 18 }, { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 20 }, { wch: 40 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Risk Register');
    XLSX.writeFile(wb, `Risk_Register_${project.name}.xlsx`);
  };

  const getMatrixRisks = () => {
    const matrix: Risk[][][] = Array(3).fill(null).map(() => Array(3).fill(null).map(() => []));
    risks.forEach(r => {
      const pCol = r.probability <= 0.33 ? 0 : r.probability <= 0.66 ? 1 : 2;
      const iRow = r.impact <= 3.33 ? 0 : r.impact <= 6.66 ? 1 : 2;
      matrix[2 - iRow][pCol].push(r);
    });
    return matrix;
  };

  const matrixData = [
    ['#fef9c3', '#fee2e2', '#fee2e2'],
    ['#dcfce7', '#fef9c3', '#fee2e2'],
    ['#dcfce7', '#dcfce7', '#fef9c3'],
  ];

  const newRisksCount = risks.filter(r => r.is_new).length;
  const suggestionsCount = risks.filter(r => r.has_suggestion).length;

  return (
    <div style={styles.container}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink} onClick={onBack}>{C.myProjects}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbLink} onClick={onBack}>{project.name}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbCurrent}>Risk Management</span>
      </div>

      <div style={styles.main}>
        {/* AI Notification Banner */}
        {(newRisksCount > 0 || suggestionsCount > 0) && (
          <div style={{ backgroundColor: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '10px', padding: '14px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px' }}>✦</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#4f46e5' }}>{L.aiBannerTitle}</span>
              <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                {newRisksCount > 0 && <span style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '500' }}>{L.aiNewRisks(newRisksCount)}</span>}
                {suggestionsCount > 0 && <span style={{ backgroundColor: '#fef9c3', color: '#854d0e', padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '500' }}>{L.aiSuggestions(suggestionsCount)}</span>}
              </div>
            </div>
          </div>
        )}

        <div style={styles.titleRow}>
          <h2 style={styles.pageTitle}>Risk Management</h2>
          <div style={styles.titleButtons}>
            <button style={styles.exportBtn} onClick={exportToExcel}>{C.exportExcel}</button>
            {canEdit && (
              <button style={styles.addBtn} onClick={() => setShowModal(true)}>{L.btnAddRisk}</button>
            )}
          </div>
        </div>

        {loading ? <p style={styles.loading}>{C.loading}</p> : (
          <>
            {/* Risk Matrix */}
            <div style={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ ...styles.sectionTitle, margin: 0 }}>Risk Matrix</h3>
                <div style={{ display: 'flex', gap: '16px' }}>
                  {[{ bg: '#dcfce7', label: L.legendLow }, { bg: '#fef9c3', label: L.legendMedium }, { bg: '#fee2e2', label: L.legendHigh }].map(l => (
                    <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: l.bg, flexShrink: 0 }} />
                      <span>{l.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'stretch', gap: '8px' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', writingMode: 'vertical-rl', transform: 'rotate(180deg)', textAlign: 'center', flexShrink: 0 }}>{L.matrixImpactAxis}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <div style={{ width: '48px', flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-around', height: '300px' }}>
                      {[L.matrixImpactHigh, L.matrixImpactMedium, L.matrixImpactLow].map(l => (
                        <div key={l} style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'right', paddingRight: '6px' }}>{l}</div>
                      ))}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(3, 100px)', gap: '4px', height: '300px' }}>
                      {getMatrixRisks().map((row, rIdx) =>
                        row.map((cell, cIdx) => (
                          <div key={`${rIdx}-${cIdx}`} style={{ backgroundColor: matrixData[rIdx][cIdx], borderRadius: '8px', padding: '8px', display: 'flex', flexDirection: 'column', gap: '4px', overflow: 'hidden' }}>
                            {cell.map((risk, i) => (
                              <div key={i} style={{ fontSize: '11px', backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '4px', padding: '3px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0 }}
                                onMouseEnter={e => { const r = e.currentTarget.getBoundingClientRect(); setHoveredRisk({ risk, x: r.left, y: r.bottom + 6 }); }}
                                onMouseLeave={() => setHoveredRisk(null)}>
                                {risk.title}
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                    <div style={{ width: '48px', flexShrink: 0 }} />
                    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
                      {[L.matrixProbLow, L.matrixProbMedium, L.matrixProbHigh].map(l => (
                        <div key={l} style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>{l}</div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                    <div style={{ width: '48px', flexShrink: 0 }} />
                    <div style={{ flex: 1, fontSize: '12px', color: '#6b7280', fontWeight: '500', textAlign: 'center' }}>{L.matrixProbabilityAxis}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Risk Register */}
            <div style={styles.section}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Risk Register</h3>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['all', 'threat', 'opportunity'] as const).map(f => {
                    const labels: Record<string, string> = language === 'en'
                      ? { all: 'All', threat: '⚠ Threats', opportunity: '✦ Opportunities' }
                      : { all: 'Όλα', threat: '⚠ Απειλές', opportunity: '✦ Ευκαιρίες' };
                    const colors: Record<string, { bg: string; active: string; text: string }> = {
                      all:         { bg: '#f1f5f9', active: '#1a1a2e', text: '#6b7280' },
                      threat:      { bg: '#fee2e2', active: '#ef4444', text: '#6b7280' },
                      opportunity: { bg: '#dcfce7', active: '#16a34a', text: '#6b7280' },
                    };
                    const c = colors[f];
                    return (
                      <button key={f} onClick={() => setTypeFilter(f)} style={{
                        padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600,
                        background: typeFilter === f ? c.active : c.bg,
                        color: typeFilter === f ? '#fff' : c.text,
                      }}>
                        {labels[f]}
                        {f !== 'all' && ` (${risks.filter(r => (r.risk_type || 'threat') === f).length})`}
                      </button>
                    );
                  })}
                </div>
              </div>
              {risks.length === 0 ? (
                <p style={styles.emptyText}>{L.emptyState}</p>
              ) : (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.tableHeader}>
                        <th style={styles.th}>#</th>
                        {L.tableHeaders.slice(0, 6).map(h => <th key={h} style={styles.th}>{h}</th>)}
                        {canEdit && <th style={styles.th}>{L.tableHeaders[6]}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {risks.filter(r => typeFilter === 'all' || (r.risk_type || 'threat') === typeFilter).map((risk, i) => {
                        const color = getScoreColor(risk.score);
                        const isOpportunity = risk.risk_type === 'opportunity';
                        return (
                          <React.Fragment key={risk.id}>
                            <tr style={{ ...styles.tableRow, backgroundColor: risk.is_new ? '#f0fdf4' : risk.has_suggestion ? '#fffbeb' : 'white' }}>
                              <td style={styles.td}>{i + 1}</td>
                              <td style={styles.td}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px', flexWrap: 'wrap' }}>
                                  <span style={styles.riskTitle}>{risk.title}</span>
                                  <span style={{
                                    fontSize: '10px', padding: '1px 7px', borderRadius: '999px', fontWeight: 700,
                                    background: isOpportunity ? '#dcfce7' : '#fee2e2',
                                    color: isOpportunity ? '#16a34a' : '#ef4444',
                                  }}>
                                    {isOpportunity ? (language === 'en' ? '✦ Opportunity' : '✦ Ευκαιρία') : (language === 'en' ? '⚠ Threat' : '⚠ Απειλή')}
                                  </span>
                                  {risk.is_new && canEdit && (
                                    <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '999px', backgroundColor: '#dcfce7', color: '#166534', fontWeight: '600', cursor: 'pointer' }} onClick={() => confirmNewRisk(risk.id)} title={L.newBadgeHint}>{L.newBadge} ✕</span>
                                  )}
                                  {risk.has_suggestion && canEdit && (
                                    <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '999px', backgroundColor: '#fef9c3', color: '#854d0e', fontWeight: '600', cursor: 'pointer' }} onClick={() => setExpandedSuggestion(expandedSuggestion === risk.id ? null : risk.id)}>
                                      ✦ AI {expandedSuggestion === risk.id ? '▲' : '▼'}
                                    </span>
                                  )}
                                </div>
                                <div style={styles.riskDesc}>{risk.description}</div>
                                {(risk.contract_reference || risk.risk_category) && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '5px' }}>
                                    {risk.risk_category && (
                                      <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '6px', ...getCategoryStyle(risk.risk_category) }}>
                                        {risk.risk_category}
                                      </span>
                                    )}
                                    {risk.contract_reference && (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#4f46e5', backgroundColor: '#ede9fe', padding: '2px 8px', borderRadius: '6px', fontWeight: '500' }}>
                                        {risk.contract_reference}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td style={styles.td}>{risk.probability}</td>
                              <td style={styles.td}>{risk.impact}</td>
                              <td style={styles.td}>
                                <span style={{ ...styles.scoreBadge, backgroundColor: color.bg, color: color.text }}>
                                  {risk.score.toFixed(2)} — {color.label}
                                </span>
                              </td>
                              <td style={styles.td}>{risk.owner || '—'}</td>
                              <td style={styles.td}>
                                <span style={{ ...styles.statusBadge, backgroundColor: risk.status === 'Closed' ? '#dcfce7' : risk.status === 'In Progress' ? '#dbeafe' : '#f3f4f6', color: risk.status === 'Closed' ? '#166534' : risk.status === 'In Progress' ? '#1e40af' : '#374151' }}>
                                  {risk.status}
                                </span>
                              </td>
                              {canEdit && (
                                <td style={styles.td}>
                                  <div style={styles.actions}>
                                    <button style={styles.editBtn} onClick={() => handleEdit(risk)}>{L.btnEditShort}</button>
                                    <button style={styles.deleteBtn} onClick={() => handleDelete(risk.id)}>{L.btnDeleteShort}</button>
                                  </div>
                                </td>
                              )}
                            </tr>
                            {risk.has_suggestion && expandedSuggestion === risk.id && risk.ai_suggestion && canEdit && (
                              <tr>
                                <td colSpan={8} style={{ padding: '0', backgroundColor: '#fffbeb' }}>
                                  <div style={{ margin: '0 16px 12px', backgroundColor: 'white', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px 16px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#854d0e', marginBottom: '10px' }}>{L.aiSuggestionTitle}</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '12px', marginBottom: '10px' }}>
                                      <div>
                                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{L.aiSuggestionNewProb}</div>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{risk.probability} → <span style={{ color: '#4f46e5' }}>{risk.ai_suggestion.probability}</span></div>
                                      </div>
                                      <div>
                                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{L.aiSuggestionNewImpact}</div>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{risk.impact} → <span style={{ color: '#4f46e5' }}>{risk.ai_suggestion.impact}</span></div>
                                      </div>
                                      <div>
                                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{L.aiSuggestionReason}</div>
                                        <div style={{ fontSize: '13px', color: '#374151' }}>{risk.ai_suggestion.reason}</div>
                                      </div>
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '3px' }}>{L.aiSuggestionNewMitigation}</div>
                                      <div style={{ fontSize: '13px', color: '#374151' }}>{risk.ai_suggestion.mitigation}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <button onClick={() => acceptSuggestion(risk)} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '6px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>{L.aiAcceptBtn}</button>
                                      <button onClick={() => rejectSuggestion(risk)} style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', padding: '6px 16px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>{L.aiRejectBtn}</button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showModal && canEdit && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{editingRisk ? L.modalEditTitle : L.modalNewTitle}</h2>
            <div style={styles.field}>
              <label style={styles.label}>{language === 'en' ? 'Type' : 'Τύπος'}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['threat', 'opportunity'] as const).map(t => (
                  <button key={t} type="button" onClick={() => setForm({ ...form, risk_type: t })} style={{
                    flex: 1, padding: '8px', borderRadius: 8, border: '2px solid',
                    borderColor: form.risk_type === t ? (t === 'threat' ? '#ef4444' : '#16a34a') : '#e5e7eb',
                    background: form.risk_type === t ? (t === 'threat' ? '#fee2e2' : '#dcfce7') : '#f9fafb',
                    color: form.risk_type === t ? (t === 'threat' ? '#ef4444' : '#16a34a') : '#6b7280',
                    cursor: 'pointer', fontWeight: 700, fontSize: 13,
                  }}>
                    {t === 'threat' ? (language === 'en' ? '⚠ Threat' : '⚠ Απειλή') : (language === 'en' ? '✦ Opportunity' : '✦ Ευκαιρία')}
                  </button>
                ))}
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldTitle}</label>
              <input style={styles.input} value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={L.fieldTitlePlaceholder} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldDescription}</label>
              <textarea style={{ ...styles.input, height: '52px', resize: 'none' }} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={L.fieldDescriptionPlaceholder} />
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldProbability(form.probability ?? 0).replace(/\*\*([\d.]+)\*\*/, '$1')}</label>
                <input type="range" min="0" max="1" step="0.1" value={form.probability || 0} onChange={e => setForm({ ...form, probability: parseFloat(e.target.value) })} style={styles.slider} />
                <div style={styles.sliderLabels}><span>0</span><span>0.5</span><span>1</span></div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldImpact(form.impact ?? 0).replace(/\*\*([\d.]+)\*\*/, '$1')}</label>
                <input type="range" min="0" max="10" step="0.5" value={form.impact || 0} onChange={e => setForm({ ...form, impact: parseFloat(e.target.value) })} style={styles.slider} />
                <div style={styles.sliderLabels}><span>0</span><span>5</span><span>10</span></div>
              </div>
            </div>
            <div style={{ ...styles.scorePreview, backgroundColor: getScoreColor((form.probability || 0) * (form.impact || 0)).bg, color: getScoreColor((form.probability || 0) * (form.impact || 0)).text }}>
              {L.fieldScorePreview(parseFloat(((form.probability || 0) * (form.impact || 0)).toFixed(2)), getScoreColor((form.probability || 0) * (form.impact || 0)).label)}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldOwner}</label>
              {stakeholderNames.length === 0 ? (
                <div style={{ ...styles.input, color: '#9ca3af', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                  {L.fieldOwnerNoStakeholders}
                </div>
              ) : (
                <select style={styles.input} value={form.owner || ''} onChange={e => setForm({ ...form, owner: e.target.value })}>
                  <option value="">{L.fieldOwnerPlaceholder}</option>
                  {stakeholderNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              )}
            </div>
            <div style={styles.field}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <label style={styles.label}>{L.fieldMitigation}</label>
                <button
                  type="button"
                  disabled={aiMitLoading || !form.title}
                  onClick={async () => {
                    setAiMitLoading(true);
                    try {
                      const res = await axios.post(`${API}/advisor/${project.id}/suggest-mitigation`, {
                        type: 'risk', title: form.title, description: form.description,
                        extra: { probability: form.probability, impact: form.impact },
                      }, { headers: { Authorization: `Bearer ${token}` } });
                      setForm(f => ({ ...f, mitigation: res.data.suggestion }));
                    } catch { } finally { setAiMitLoading(false); }
                  }}
                  style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: '1px solid #6366f1', background: aiMitLoading ? '#e0e7ff' : '#eef2ff', color: '#4f46e5', cursor: form.title ? 'pointer' : 'not-allowed', fontWeight: 600 }}
                >
                  {aiMitLoading ? '...' : L.fieldMitigationAI}
                </button>
              </div>
              <textarea style={{ ...styles.input, height: '52px', resize: 'none' }} value={form.mitigation || ''} onChange={e => setForm({ ...form, mitigation: e.target.value })} placeholder={L.fieldMitigationPlaceholder} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldStatus}</label>
              <select style={styles.select} value={form.status || 'Open'} onChange={e => setForm({ ...form, status: e.target.value as Risk['status'] })}>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldComments}</label>
              <textarea style={{ ...styles.input, height: '52px', resize: 'none' }} value={form.comments || ''} onChange={e => setForm({ ...form, comments: e.target.value })} />
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={resetForm}>{C.cancel}</button>
              <button style={styles.saveBtn} onClick={handleSubmit} disabled={saving}>{saving ? C.saving : C.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Risk hover popup */}
      {hoveredRisk && (() => {
        const { risk, x, y } = hoveredRisk;
        const color = getScoreColor(risk.score);
        const statusColors: Record<string, { bg: string; text: string }> = {
          'Open': { bg: '#fee2e2', text: '#991b1b' },
          'In Progress': { bg: '#dbeafe', text: '#1e40af' },
          'Closed': { bg: '#f3f4f6', text: '#6b7280' },
        };
        const sc = statusColors[risk.status] || statusColors['Open'];
        const popupWidth = 320;
        const left = Math.min(x, window.innerWidth - popupWidth - 16);
        const top = y + window.scrollY;
        return (
          <div style={{ position: 'fixed', top, left, width: popupWidth, backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid #e5e7eb', zIndex: 9999, padding: '14px 16px', pointerEvents: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#1a1a2e', lineHeight: 1.4 }}>{risk.title}</div>
              <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px', backgroundColor: sc.bg, color: sc.text, flexShrink: 0 }}>{risk.status}</span>
            </div>
            {risk.description && <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px', lineHeight: 1.5 }}>{risk.description}</div>}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: color.bg, color: color.text, fontWeight: 600 }}>Score: {risk.score.toFixed(1)} — {color.label}</span>
              {risk.risk_category && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#ede9fe', color: '#4f46e5' }}>{risk.risk_category}</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px' }}>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>{`${L.fieldProbability}: `}<span style={{ color: '#374151', fontWeight: 500 }}>{(risk.probability * 100).toFixed(0)}%</span></div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>{`${L.fieldImpact}: `}<span style={{ color: '#374151', fontWeight: 500 }}>{risk.impact}/10</span></div>
              {risk.owner && <div style={{ fontSize: '11px', color: '#9ca3af' }}>{`${L.fieldOwner}: `}<span style={{ color: '#374151', fontWeight: 500 }}>{risk.owner}</span></div>}
            </div>
            {risk.mitigation && (
              <div style={{ fontSize: '11px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '6px 8px', color: '#166534' }}>
                <span style={{ fontWeight: 600 }}>{L.fieldMitigation}:</span> {risk.mitigation}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { color: 'white', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: { color: 'white', fontSize: '14px' },
  userRole: { backgroundColor: '#4f46e5', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  breadcrumb: { padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky' as const, top: 64, zIndex: 99 },
  breadcrumbLink: { color: '#4f46e5', cursor: 'pointer', fontWeight: '500' },
  breadcrumbSep: { color: '#9ca3af', margin: '0 8px' },
  breadcrumbCurrent: { color: '#374151', fontWeight: '500' },
  main: { padding: '32px 48px', width: '100%', boxSizing: 'border-box' },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
  pageTitle: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  titleButtons: { display: 'flex', gap: '12px' },
  exportBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  addBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  loading: { color: '#666' },
  section: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', marginBottom: '20px' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 16px 0' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f9fafb' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  tableRow: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#374151', verticalAlign: 'top' },
  riskTitle: { fontWeight: '500' },
  riskDesc: { fontSize: '12px', color: '#9ca3af', marginTop: '2px' },
  scoreBadge: { padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' },
  statusBadge: { padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  actions: { display: 'flex', gap: '8px' },
  editBtn: { backgroundColor: '#ede9fe', color: '#4f46e5', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' },
  deleteBtn: { backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' },
  emptyText: { color: '#9ca3af', fontSize: '14px' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '580px', maxHeight: '70vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '17px', fontWeight: '600', color: '#1a1a2e', marginBottom: '16px' },
  field: { marginBottom: '10px' },
  label: { display: 'block', marginBottom: '4px', fontWeight: '500', color: '#333', fontSize: '13px' },
  input: { width: '100%', padding: '7px 10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  select: { width: '100%', padding: '7px 10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', outline: 'none', backgroundColor: 'white' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  slider: { width: '100%', marginTop: '8px' },
  sliderLabels: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af', marginTop: '2px' },
  scorePreview: { padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', textAlign: 'center', marginBottom: '16px' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default RiskManagement;
